import React, { useState, useEffect, useRef } from 'react';
import { Newspaper, Clock } from 'lucide-react';
import { Language, Translations } from '../i18n/translations';

const localeMap: Record<Language, string> = {
  en: 'en-GB',
  pl: 'pl-PL',
};

interface HeaderProps {
  timeRemaining: string;
  timeSpentToday: number;
  dailyTimeLimit: number;
  language: Language;
  t: Translations;
}

// Announce remaining time to screen readers only at meaningful thresholds.
// Polite live region was previously firing every minute, which is noisy.
// Sorted ascending so we can find the smallest matching threshold = the
// currently-active "warning level."
const ANNOUNCE_THRESHOLDS_MIN = [1, 5, 10];

export const Header: React.FC<HeaderProps> = ({
  timeRemaining,
  timeSpentToday,
  dailyTimeLimit,
  language,
  t,
}) => {
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const lastAnnouncedThresholdRef = useRef<number | null>(null);

  const minutesRemaining = dailyTimeLimit > 0
    ? Math.ceil(Math.max(0, dailyTimeLimit * 60 - timeSpentToday) / 60)
    : null;

  // Decide whether the live region should announce this render.
  let shouldAnnounce = false;
  if (minutesRemaining !== null) {
    const crossed = ANNOUNCE_THRESHOLDS_MIN.find(threshold => minutesRemaining <= threshold);
    if (crossed !== undefined && lastAnnouncedThresholdRef.current !== crossed) {
      shouldAnnounce = true;
      lastAnnouncedThresholdRef.current = crossed;
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyHeader(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const today = new Date();
  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };

  return (
    <>
      {/* Main Header */}
      <header ref={headerRef} className="bg-white dark:bg-gray-800 border-b-4 sm:border-b-8 border-black dark:border-gray-600" role="banner">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1 sm:mb-2">
              <Newspaper className="w-6 h-6 sm:w-10 sm:h-10 text-gray-900 dark:text-white" aria-hidden="true" />
            </div>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold font-serif tracking-tight text-gray-900 dark:text-white">
              {t.appTitle}
              <sup className="text-xs sm:text-sm font-sans font-medium text-gray-500 dark:text-gray-400 ml-1">beta</sup>
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1 sm:mt-2">
              {t.appSubtitle} • <time dateTime={today.toISOString().split('T')[0]}>{today.toLocaleDateString(localeMap[language], dateOptions)}</time>
            </p>
            {dailyTimeLimit > 0 && (
              <div
                className="flex items-center justify-center gap-1 mt-2 text-sm"
                aria-live={shouldAnnounce ? 'polite' : 'off'}
              >
                <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />
                <span className={`font-medium ${timeSpentToday >= dailyTimeLimit * 60 * 0.8 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                  {timeRemaining}{t.remainingToday}
                </span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Sticky Compact Header */}
      <div
        className={`fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 border-b-2 border-black dark:border-gray-600 z-50 transition-transform duration-200 ${
          showStickyHeader ? 'translate-y-0' : '-translate-y-full'
        }`}
        aria-hidden={!showStickyHeader}
      >
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-gray-900 dark:text-white" aria-hidden="true" />
              <span className="text-lg font-bold font-serif text-gray-900 dark:text-white">
                {t.appTitle}
                <sup className="text-xs font-sans font-medium text-gray-500 dark:text-gray-400 ml-0.5">beta</sup>
              </span>
            </div>
            {dailyTimeLimit > 0 && (
              <div className="flex items-center gap-1 text-xs">
                <Clock className="w-3 h-3 text-gray-500 dark:text-gray-400" aria-hidden="true" />
                <span className={`font-medium ${timeSpentToday >= dailyTimeLimit * 60 * 0.8 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                  {timeRemaining}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
